import React, { memo, type ComponentType } from 'react';
import { graphql, useFragment } from 'react-relay';
import IncidentManagementProductSettingNavMenuItemOnboarding from '@jira/servicedesk__incident-management-settings/src/IncidentManagementProductSettingNavMenuItemOnboarding.tsx';
import { isCMDBEnabledInEnvironment } from '@jira/servicedesk__insight-common-utils-cmdb-fedramp/src/isCMDBEnabledInEnvironment.tsx';
import {
	ExpandableMenuItem,
	ExpandableMenuItemContent,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { MenuSection, MenuSectionHeading } from '@atlaskit/side-nav-items/menu-section';
// eslint-disable-next-line jira/restricted/fedramp-isolated-cloud
import { isFedRamp } from '@atlassian/atl-context';
import { LICENSED_PRODUCTS } from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import { functionUnionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useIsFullyExpanded } from '@jira/navigation-apps__sidebar-common/src/controllers/is-fully-expanded/index.tsx';
import ExpandableMenuItemTrigger from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/expandable-menu-item-trigger-with-analytics/index.tsx';
import LinkMenuItem from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/link-menu-item-with-analytics/index.tsx';
import { useCurrentRoute } from '@jira/platform__utils-router/src/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import type { Features } from '../../common/types.tsx';
import type { ServiceManagementSection_queryNavigationAppsSidebarNav4SidebarsSettingsGlobalProducts$key } from './__generated__/ServiceManagementSection_queryNavigationAppsSidebarNav4SidebarsSettingsGlobalProducts.graphql';
import { COMMON_ANALYTICS_ATTRIBUTES } from './constants.tsx';

const heading = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-management-section.heading',

	defaultMessage: 'Jira Service Management',
	description: 'Navigation group heading for Jira Service Management settings items',
});

const configurationItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-management-section.configuration-item',

	defaultMessage: 'Configuration',
	description: 'Navigation item which links to Jira Service Management configuration page',
});

const complianceSettingsItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-management-section.compliance-settings-item',

	defaultMessage: 'Compliance settings',
	description: 'Navigation item which links to Jira Service Management compliance page',
});

const emailRequestsItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-management-section.email-requests-item',

	defaultMessage: 'Email requests',
	description:
		'Navigation item which links to a Jira Service Management email requests settings page',
});

const customerAccessItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-management-section.customer-access-item',

	defaultMessage: 'Customer access',
	description:
		'Navigation item which links to a Jira Service Management Customer access settings page',
});

const helpCenterPermissionsItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-management-section.help-center-permissions-item',

	defaultMessage: 'Help center permissions',
	description:
		'Navigation item which links to a Jira Service Management Help center permissions settings page',
});

const incidentManagementItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-management-section.incident-management-item',

	defaultMessage: 'Incident management',
	description: 'Navigation item which links to a Jira Service incident management',
});

const knowledgeBasePermissionsItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-management-section.knowledge-base-permissions-item',

	defaultMessage: 'Knowledge base permissions',
	description: 'Navigation item which links to a Jira Service Management Knowledge Base Settings',
});

const authenticationItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-management-section.authentication-item',

	defaultMessage: 'Authentication',
	description:
		'Navigation item which links to a Jira Service Management Authentication settings page',
});

const organizationsItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-management-section.organizations-item',

	defaultMessage: 'Organizations',
	description: 'Navigation item which links to the Organizations List page',
});

const featureUsageItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-management-section.feature-usage-item',

	defaultMessage: 'Feature usage',
	description:
		'Navigation item which links to a Jira Service Management Feature Usage settings page',
});

const workforceSkillsItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-management-section.workforce-skills-item',

	defaultMessage: 'Skills directory',
	description:
		'Navigation item which links to the Jira Service Management workforce skills directory page',
});

const storageManagementItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-management-section.storage-management-item',
	defaultMessage: 'Storage management',
	description: 'Navigation item which links to the Jira Service Management storage management page',
});

const aiGovernanceItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-management-section.ai-governance-item',
	defaultMessage: 'AI governance',
	description: 'Navigation item group for Jira Service Management AI governance pages',
});

const aiControlsItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-management-section.ai-controls-item',
	defaultMessage: 'AI controls',
	description: 'Navigation item group for Jira Service Management AI controls pages',
});

const nativeAiCapabilitiesItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-management-section.native-ai-capabilities-item',
	defaultMessage: 'Native app AI capabilities',
	description:
		'Navigation item which links to Jira Service Management native app AI capabilities page',
});

const AI_GOVERNANCE_ROUTES = new Set(['products-jsm-ai-governance-native-ai-capabilities']);
const AI_CONTROLS_ROUTES = new Set(['products-jsm-ai-governance-native-ai-capabilities']);

const useIsFullyExpandedConditional = functionUnionWithCondition(
	() => fg('fe_stakeholder_email_templates_in_jsm'),
	useIsFullyExpanded,
	() => false,
);

type Props = {
	features: Features;
	queryFragment?: ServiceManagementSection_queryNavigationAppsSidebarNav4SidebarsSettingsGlobalProducts$key;
};

const ServiceManagementSectionInternal = ({
	features,
	queryFragment,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const currentRoute = useCurrentRoute();
	const { licensedProducts } = useTenantContext();
	const queryData =
		useFragment<ServiceManagementSection_queryNavigationAppsSidebarNav4SidebarsSettingsGlobalProducts$key>(
			graphql`
				fragment ServiceManagementSection_queryNavigationAppsSidebarNav4SidebarsSettingsGlobalProducts on Query
				@argumentDefinitions(cloudId: { type: "ID!" }, skipSiteWfoStatus: { type: "Boolean!" }) {
					agentWorkspace_siteWfoStatus(cloudId: $cloudId)
						@optIn(to: "AgentWorkspace")
						@skip(if: $skipSiteWfoStatus) {
						hasProjectEnabledWfo
					}
				}
			`,
			queryFragment,
		);

	const isFullyExpanded = useIsFullyExpandedConditional();
	const hasConfluence = Boolean(licensedProducts[LICENSED_PRODUCTS.CONFLUENCE]);
	const hasProjectEnabledWfo =
		queryData?.agentWorkspace_siteWfoStatus?.hasProjectEnabledWfo ?? false;
	const isOnAiGovernanceRoute = AI_GOVERNANCE_ROUTES.has(currentRoute.name);
	const isOnAiControlsRoute = AI_CONTROLS_ROUTES.has(currentRoute.name);

	const incidentManagementServiceDeskMenuItem = (
		<LinkMenuItem
			analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'incidentManagement' }}
			isSelected={currentRoute.name === 'products-jsm-incident-management-tools'}
			href="/jira/settings/products/jira-service-management-incident-management-tools"
		>
			{formatMessage(incidentManagementItem)}
		</LinkMenuItem>
	);

	return (
		<MenuSection isMenuListItem>
			<MenuSectionHeading>{formatMessage(heading)}</MenuSectionHeading>
			<MenuList>
				{!fg('csm_appification_jira_admin') && features.hasServiceManagementConfig && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'configuration' }}
						isSelected={currentRoute.name === 'products-jsm-configuration'}
						href="/jira/settings/products/jira-service-management-configuration"
					>
						{formatMessage(configurationItem)}
					</LinkMenuItem>
				)}
				{fg('jsmgrowth-267_-_enforce_storage_limits') && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'storageManagement' }}
						isSelected={String(currentRoute.name) === 'products-jsm-storage-management'}
						href="/jira/settings/products/jira-service-management-storage-management"
					>
						{formatMessage(storageManagementItem)}
					</LinkMenuItem>
				)}
				{fg('enable-jsm-native-app-ai-capabilities') && (
					<ExpandableMenuItem isDefaultExpanded={isOnAiGovernanceRoute}>
						<ExpandableMenuItemTrigger
							analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'aiGovernance' }}
							interactionName="nav4-global-products-settings-ai-governance"
						>
							{formatMessage(aiGovernanceItem)}
						</ExpandableMenuItemTrigger>
						<ExpandableMenuItemContent>
							<ExpandableMenuItem isDefaultExpanded={isOnAiControlsRoute}>
								<ExpandableMenuItemTrigger
									analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'aiControls' }}
									interactionName="nav4-global-products-settings-ai-controls"
								>
									{formatMessage(aiControlsItem)}
								</ExpandableMenuItemTrigger>
								<ExpandableMenuItemContent>
									<LinkMenuItem
										analytics={{
											...COMMON_ANALYTICS_ATTRIBUTES,
											itemId: 'nativeAiCapabilities',
										}}
										isSelected={
											currentRoute.name === 'products-jsm-ai-governance-native-ai-capabilities'
										}
										href="/jira/settings/products/jira-service-management-ai-governance/native-ai-capabilities"
									>
										{formatMessage(nativeAiCapabilitiesItem)}
									</LinkMenuItem>
								</ExpandableMenuItemContent>
							</ExpandableMenuItem>
						</ExpandableMenuItemContent>
					</ExpandableMenuItem>
				)}
				{!fg('csm_appification_jira_admin') && features.hasServiceManagementEmailRequests && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'emailRequests' }}
						isSelected={currentRoute.name === 'products-jsm-email-requests'}
						href="/jira/settings/products/jira-service-management-email-requests"
					>
						{formatMessage(emailRequestsItem)}
					</LinkMenuItem>
				)}
				{features.hasServiceManagementCustomerAccess && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'customerAccess' }}
						isSelected={currentRoute.name === 'products-jsm-customer-access'}
						href="/jira/settings/products/servicedesk/customer-access"
					>
						{formatMessage(
							fg('csm_appification_jira_admin') ? helpCenterPermissionsItem : customerAccessItem,
						)}
					</LinkMenuItem>
				)}
				{!fg('csm_appification_jira_admin') &&
					features.hasServiceManagementAuthenticationSettings && (
						<LinkMenuItem
							analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'authentication' }}
							isSelected={currentRoute.name === 'products-jsm-authentication-settings'}
							href="/jira/settings/products/servicedesk/authentication"
						>
							{formatMessage(authenticationItem)}
						</LinkMenuItem>
					)}
				{!fg('csm_appification_jira_admin') && features.hasOrganizationsListAccess && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'organizations' }}
						isSelected={currentRoute.name === 'products-jsm-organizations'}
						href="/jira/settings/products/servicedesk/organizations"
					>
						{formatMessage(organizationsItem)}
					</LinkMenuItem>
				)}
				{features.hasServiceManagementWorkforceSkills && hasProjectEnabledWfo && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'workforceSkills' }}
						isSelected={currentRoute.name === 'products-jsm-workforce-skills'}
						href="/jira/settings/products/jira-service-management-workforce-skills"
					>
						{formatMessage(workforceSkillsItem)}
					</LinkMenuItem>
				)}
				{features.hasServiceManagementIncidentManagementTools &&
					(fg('fe_stakeholder_email_templates_in_jsm') ? (
						<IncidentManagementProductSettingNavMenuItemOnboarding
							isFullyExpanded={isFullyExpanded}
						>
							{incidentManagementServiceDeskMenuItem}
						</IncidentManagementProductSettingNavMenuItemOnboarding>
					) : (
						incidentManagementServiceDeskMenuItem
					))}
				{!fg('csm_appification_jira_admin') &&
					hasConfluence &&
					features.hasServiceManagementKbPermissionsAccess && (
						<LinkMenuItem
							analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'knowledgeBasePermissions' }}
							isSelected={currentRoute.name === 'products-jsm-kb-permissions'}
							href="/jira/settings/products/jira-service-management-knowledge-base-permissions"
						>
							{formatMessage(knowledgeBasePermissionsItem)}
						</LinkMenuItem>
					)}
				{!fg('csm_appification_jira_admin') && features.hasServiceManagementConfig && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'complianceSettings' }}
						isSelected={currentRoute.name === 'products-jsm-compliance'}
						href="/jira/settings/products/jira-service-management-compliance"
					>
						{formatMessage(complianceSettingsItem)}
					</LinkMenuItem>
				)}
				{(isCMDBEnabledInEnvironment() || !isFedRamp()) && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'featureUsage' }}
						isSelected={currentRoute.name === 'products-jsm-feature-usage'}
						href="/jira/settings/products/servicedesk/feature-usage"
					>
						{formatMessage(featureUsageItem)}
					</LinkMenuItem>
				)}
			</MenuList>
		</MenuSection>
	);
};

export const ServiceManagementSection: ComponentType<Props> = memo(
	ServiceManagementSectionInternal,
);
